import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { VipTaskModel, VipTaskItemModel, VipLevelModel, VipLevelRewardModel } from '@shared/types';
import { deepCopyMap } from '@shared/utils';

import { VipSystemState } from './types';

const initialState: VipSystemState = {
  vipTasks: [],
  vipTaskItemsMap: new Map(),
  vipLevels: [],
  vipLevelRewardsMap: new Map(),
};

export const vipSystemSlice = createSlice({
  name: 'vipSystemSlice',
  initialState,
  reducers: {
    setVipTasks: (state: VipSystemState, { payload }: PayloadAction<VipTaskModel[]>) => {
      state.vipTasks = payload;
    },
    updateVipTasks: (state: VipSystemState, { payload }: PayloadAction<VipTaskModel>) => {
      const mappedVipTasks = state.vipTasks.map((vipTask) => {
        const { id: vipTaskId } = vipTask;
        const { id: payloadVipTaskId } = payload;

        if (vipTaskId === payloadVipTaskId) {
          return payload;
        }

        return vipTask;
      });

      state.vipTasks = mappedVipTasks;
    },
    setVipTask: (state: VipSystemState, { payload }: PayloadAction<VipTaskModel>) => {
      state.vipTasks = [payload, ...state.vipTasks];
    },
    deleteVipTask: (state: VipSystemState, { payload: payloadVipTaskId }: PayloadAction<string>) => {
      const filteredVipTasks = state.vipTasks.filter((vipTask) => {
        const { id: vipTaskId } = vipTask;

        return payloadVipTaskId !== vipTaskId;
      });

      state.vipTasks = filteredVipTasks;
    },
    setVipTaskItems: (
      state: VipSystemState,
      {
        payload,
      }: PayloadAction<{
        vipTaskItems: VipTaskItemModel[];
        vipTaskId: string;
      }>
    ) => {
      const { vipTaskId, vipTaskItems } = payload;

      const copiedMap = deepCopyMap<string, VipTaskItemModel[]>(state.vipTaskItemsMap);

      copiedMap.set(vipTaskId, vipTaskItems);

      state.vipTaskItemsMap = copiedMap;
    },
    updateVipTaskItems: (state: VipSystemState, { payload }: PayloadAction<VipTaskItemModel>) => {
      const { vipTaskId } = payload;

      const copiedMap = deepCopyMap<string, VipTaskItemModel[]>(state.vipTaskItemsMap);
      const vipTaskItems = copiedMap.get(vipTaskId) ?? [];

      const mappedTaskItems = vipTaskItems.map((betOutcome) => {
        const { id: betOutcomeId } = betOutcome;
        const { id: payloadTaskItemId } = payload;

        if (betOutcomeId === payloadTaskItemId) {
          return payload;
        }

        return betOutcome;
      });

      copiedMap.set(vipTaskId, mappedTaskItems);

      state.vipTaskItemsMap = copiedMap;
    },
    setVipTaskItem: (state: VipSystemState, { payload }: PayloadAction<VipTaskItemModel>) => {
      const { vipTaskId } = payload;

      const copiedMap = deepCopyMap<string, VipTaskItemModel[]>(state.vipTaskItemsMap);
      const vipTaskItems = copiedMap.get(vipTaskId) ?? [];

      const copiedVipTaskItems = [...vipTaskItems, payload];

      copiedMap.set(vipTaskId, copiedVipTaskItems);

      state.vipTaskItemsMap = copiedMap;
    },
    deleteVipTaskItems: (
      state: VipSystemState,
      { payload }: PayloadAction<{ vipTaskItemId: string; vipTaskId: string }>
    ) => {
      const { vipTaskId, vipTaskItemId } = payload;

      const copiedMap = deepCopyMap<string, VipTaskItemModel[]>(state.vipTaskItemsMap);
      const vipTaskItems = copiedMap.get(vipTaskId) ?? [];

      const filteredTaskItems = vipTaskItems.filter((betOutcome) => {
        const { id: currentTaskItemId } = betOutcome;

        return vipTaskItemId !== currentTaskItemId;
      });

      copiedMap.set(vipTaskId, filteredTaskItems);

      state.vipTaskItemsMap = copiedMap;
    },
    setVipLevels: (state: VipSystemState, { payload }: PayloadAction<VipLevelModel[]>) => {
      state.vipLevels = payload;
    },
    updateVipLevels: (state: VipSystemState, { payload }: PayloadAction<VipLevelModel>) => {
      const mappedVipLevels = state.vipLevels.map((vipLevel) => {
        const { id: vipLevelId } = vipLevel;
        const { id: payloadVipLevelId } = payload;

        if (vipLevelId === payloadVipLevelId) {
          return payload;
        }

        return vipLevel;
      });

      state.vipLevels = mappedVipLevels;
    },
    setVipLevel: (state: VipSystemState, { payload }: PayloadAction<VipLevelModel>) => {
      const copiedArray = [...state.vipLevels];

      copiedArray.push(payload);

      state.vipLevels = copiedArray;
    },
    deleteVipLevel: (state: VipSystemState, { payload: payloadVipLevelId }: PayloadAction<string>) => {
      const filteredVipLevels = state.vipLevels.filter((vipLevel) => {
        const { id: vipLevelId } = vipLevel;

        return payloadVipLevelId !== vipLevelId;
      });

      state.vipLevels = filteredVipLevels;
    },
    setVipLevelRewards: (
      state: VipSystemState,
      {
        payload,
      }: PayloadAction<{
        vipLevelRewards: VipLevelRewardModel[];
        vipLevelId: string;
      }>
    ) => {
      const { vipLevelId, vipLevelRewards } = payload;

      const copiedMap = deepCopyMap<string, VipLevelRewardModel[]>(state.vipLevelRewardsMap);

      copiedMap.set(vipLevelId, vipLevelRewards);

      state.vipLevelRewardsMap = copiedMap;
    },
    updateVipLevelRewards: (state: VipSystemState, { payload }: PayloadAction<VipLevelRewardModel>) => {
      const { vipLevelId } = payload;

      const copiedMap = deepCopyMap<string, VipLevelRewardModel[]>(state.vipLevelRewardsMap);
      const vipLevelRewards = copiedMap.get(vipLevelId) ?? [];

      const mappedLevelRewards = vipLevelRewards.map((levelReward) => {
        const { id: levelRewardId } = levelReward;
        const { id: payloadLevelRewardId } = payload;

        if (levelRewardId === payloadLevelRewardId) {
          return payload;
        }

        return levelReward;
      });

      copiedMap.set(vipLevelId, mappedLevelRewards);

      state.vipLevelRewardsMap = copiedMap;
    },
    setVipLevelReward: (state: VipSystemState, { payload }: PayloadAction<VipLevelRewardModel>) => {
      const { vipLevelId } = payload;

      const copiedMap = deepCopyMap<string, VipLevelRewardModel[]>(state.vipLevelRewardsMap);
      const vipLevelRewards = copiedMap.get(vipLevelId) ?? [];

      const copiedVipLevelRewards = [...vipLevelRewards, payload];

      copiedMap.set(vipLevelId, copiedVipLevelRewards);

      state.vipLevelRewardsMap = copiedMap;
    },
    deleteVipLevelRewards: (
      state: VipSystemState,
      { payload }: PayloadAction<{ vipLevelRewardId: string; vipLevelId: string }>
    ) => {
      const { vipLevelId, vipLevelRewardId } = payload;

      const copiedMap = deepCopyMap<string, VipLevelRewardModel[]>(state.vipLevelRewardsMap);
      const vipLevelRewards = copiedMap.get(vipLevelId) ?? [];

      const filteredLevelRewards = vipLevelRewards.filter((levelReward) => {
        const { id: currentLevelRewardId } = levelReward;

        return vipLevelRewardId !== currentLevelRewardId;
      });

      copiedMap.set(vipLevelId, filteredLevelRewards);

      state.vipLevelRewardsMap = copiedMap;
    },
  },
});

export const {
  setVipTasks,
  setVipTask,
  updateVipTasks,
  deleteVipTask,
  setVipTaskItems,
  setVipTaskItem,
  deleteVipTaskItems,
  updateVipTaskItems,
  setVipLevels,
  setVipLevel,
  updateVipLevels,
  deleteVipLevel,
  setVipLevelRewards,
  setVipLevelReward,
  deleteVipLevelRewards,
  updateVipLevelRewards,
} = vipSystemSlice.actions;

export const vipSystemSliceReducer = vipSystemSlice.reducer;
